import { Box } from '@mui/material';
import { WarningCircle } from '@phosphor-icons/react';
import React, { useMemo } from 'react';
import { RunLogDocument } from '../../../service/AdminSpaceLoggingService';
import {
  collectRunLogErrors,
  RUN_LOG_EXPAND_EVENT,
  RunLogError,
  RunLogExpandEventDetail,
} from './runLogMarkdown';

interface RunErrorSummaryProps {
  doc: RunLogDocument;
}

const dispatchExpand = (error: RunLogError): void => {
  const detail: RunLogExpandEventDetail = {
    pathIds: error.pathIds,
    deepestId: error.targetId,
  };
  window.dispatchEvent(new CustomEvent(RUN_LOG_EXPAND_EVENT, { detail }));
};

interface ErrorRowProps {
  error: RunLogError;
}

const ErrorRow: React.FC<ErrorRowProps> = ({ error }) => {
  const handleClick = () => dispatchExpand(error);
  return (
    <Box
      component='button'
      type='button'
      onClick={handleClick}
      className='sc-summary-row'
      data-kind={error.kind}
    >
      <span className='sc-summary-step'>{error.stepPath}</span>
      <span className='sc-summary-flow-path'>
        {error.flowPath.map((name, i) => (
          <React.Fragment key={`${name}-${i}`}>
            {i > 0 && <span className='sc-summary-arrow'>›</span>}
            <span className='sc-summary-flow-name'>{name}</span>
          </React.Fragment>
        ))}
      </span>
      <span className='sc-summary-arrow'>→</span>
      <span className='sc-summary-node-label'>{error.nodeLabel}</span>
      <code className='sc-summary-node-type'>{error.nodeType}</code>
      {error.errorCategory && (
        <span className='sc-summary-category'>{error.errorCategory}</span>
      )}
      {error.errorField && (
        <span className='sc-summary-field'>field: {error.errorField}</span>
      )}
      <span className='sc-summary-em-dash'>—</span>
      <span className='sc-summary-message'>{error.errorMessage}</span>
    </Box>
  );
};

const RunErrorSummary: React.FC<RunErrorSummaryProps> = ({ doc }) => {
  const errors = useMemo(() => collectRunLogErrors(doc), [doc]);
  const errorCount = errors.length;
  const hasErrors = errorCount > 0;
  return (
    <Box
      component='details'
      open={hasErrors}
      data-has-errors={hasErrors ? 'true' : 'false'}
      sx={{
        m: '0 0 14px',
        borderRadius: 2,
        border: '1px solid',
        borderLeftWidth: '5px',
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        borderColor: hasErrors ? '#fecaca' : '#e5e7eb',
        borderLeftColor: hasErrors ? '#dc2626' : '#9ca3af',
        '& > summary': {
          listStyle: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: 2,
          py: 1.25,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: 13.5,
          fontWeight: 600,
          color: hasErrors ? '#991b1b' : '#374151',
          background: hasErrors
            ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
            : '#f9fafb',
          transition: 'background 120ms ease',
          '&::-webkit-details-marker': { display: 'none' },
          '&::before': {
            content: '"▸"',
            color: hasErrors ? '#dc2626' : '#9ca3af',
            fontSize: 12,
            width: 12,
            textAlign: 'center',
            transition: 'transform 120ms ease',
          },
          '&:hover': {
            background: hasErrors
              ? 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)'
              : '#f3f4f6',
          },
        },
        '&[open] > summary::before': { content: '"▾"' },
        '&[open] > summary': {
          borderBottom: hasErrors
            ? '1px solid #fecaca'
            : '1px solid #e5e7eb',
        },
        '& .sc-summary-title': {
          letterSpacing: '0.02em',
        },
        '& .sc-summary-count': {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 12,
          fontWeight: 600,
          background: hasErrors ? '#fee2e2' : '#f3f4f6',
          border: '1px solid',
          borderColor: hasErrors ? '#fecaca' : '#e5e7eb',
          color: hasErrors ? '#991b1b' : '#6b7280',
          px: 0.75,
          py: '1px',
          borderRadius: 0.5,
        },
        '& .sc-summary-empty': {
          px: 2,
          py: 1.25,
          color: '#6b7280',
          fontSize: 13,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        '& .sc-summary-list': {
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          gap: '4px',
        },
        '& .sc-summary-row': {
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          px: 1.25,
          py: 1,
          borderRadius: 1,
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 12.5,
          color: '#1f2937',
          background: '#fff',
          border: '1px solid #fecaca',
          borderLeft: '3px solid #dc2626',
          textAlign: 'left',
          transition: 'background 120ms ease, border-color 120ms ease',
          '&:hover': {
            background: '#fef2f2',
            borderColor: '#fca5a5',
            borderLeftColor: '#b91c1c',
          },
          '&:focus-visible': {
            outline: '2px solid #2563eb',
            outlineOffset: 1,
          },
        },
        '& .sc-summary-row[data-kind="subRun"]': {
          borderLeftStyle: 'double',
          borderLeftWidth: '5px',
        },
        '& .sc-summary-step': {
          color: '#6b7280',
          fontWeight: 600,
          minWidth: 28,
        },
        '& .sc-summary-flow-path': {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          flexWrap: 'wrap',
        },
        '& .sc-summary-flow-name': {
          color: '#3730a3',
          fontWeight: 600,
          background: '#eef2ff',
          border: '1px solid #c7d2fe',
          px: 0.625,
          py: '1px',
          borderRadius: 0.5,
          fontSize: 11.5,
        },
        '& .sc-summary-arrow': {
          color: '#9ca3af',
          fontWeight: 700,
        },
        '& .sc-summary-node-label': {
          color: '#111827',
          fontWeight: 600,
        },
        '& .sc-summary-node-type': {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 11.5,
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: 0.5,
          px: 0.625,
          py: '1px',
          color: '#374151',
        },
        '& .sc-summary-category': {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 11.5,
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: 0.5,
          px: 0.625,
          py: '1px',
          color: '#92400e',
          fontWeight: 600,
        },
        '& .sc-summary-field': {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 11.5,
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: 0.5,
          px: 0.625,
          py: '1px',
          color: '#374151',
        },
        '& .sc-summary-em-dash': {
          color: '#9ca3af',
        },
        '& .sc-summary-message': {
          color: '#991b1b',
          flex: 1,
          minWidth: 0,
          wordBreak: 'break-word',
        },
      }}
    >
      <Box component='summary'>
        <WarningCircle
          size={16}
          weight='fill'
          color={hasErrors ? '#dc2626' : '#9ca3af'}
        />
        <span className='sc-summary-title'>Summary</span>
        <span className='sc-summary-count'>
          {errorCount} {errorCount === 1 ? 'error' : 'errors'}
        </span>
      </Box>
      {hasErrors ? (
        <Box className='sc-summary-list'>
          {errors.map(error => (
            <ErrorRow key={error.targetId} error={error} />
          ))}
        </Box>
      ) : (
        <Box className='sc-summary-empty'>No errors in this run.</Box>
      )}
    </Box>
  );
};

export default RunErrorSummary;
